import { Partner, Site, Device, Network, SiteGroup, Market } from '@/graphql/model/index';
import lodash from 'lodash';
import hash from 'object-hash';
import GQ from '@/graphql/GQ';
import QueryBuilder from '@/graphql/QueryBuilder';
import Vue from 'vue';

const namespace = 'scrubRateHourlyReport';

export default {

    getBaseData({ commit, dispatch, state }) {

        return GQ.query({
            sites: Site.scrubRateHourlyQuery({ filters: { status: '!ACTIVE' }, orderBy: 'name' }),
            sources: Partner.scrubRateHourlySourceQuery({ filters: { type: '!BUYING' }, orderBy: 'company' }),
            partners: Partner.scrubRateHourlyPartnerQuery({ filters: { typeNot: '!BUYING' }, orderBy: 'company' }),
            devices: Device.all({ orderBy: { id: 'asc' } }),
            markets: Market.all({ orderBy: 'name' }),
            networks: Network.all({ orderBy: 'name' }),
            siteGroups: SiteGroup.all({ orderBy: 'name' }),
        })
            .then(function (result) {
                return result;
            })
            .catch(function (errorResponse) {
                let message = [];
                errorResponse.errors.forEach(function (error) {
                    message.push(error.message);
                })

                Vue.prototype.$notification.showWarning({
                    title: 'API Error',
                    message: message.join("<br/>"),
                    timeout: 10000
                });
            })
    },

    getReportDataFromAPI({ dispatch, state }, filters) {

        if (!filters.isBaseDataLoaded) {
            return new Promise(function (resolve, reject) {
                return resolve({});
            });
        }

        //PAGINATION       
        let pagination = {}; 
        let filterPagination = filters.paginationData.pagination;
        if (filterPagination.direction && filterPagination.cursor) {
            pagination[filterPagination.direction] = filterPagination.cursor;
            if( filterPagination.direction == 'before' ) {
                pagination['last'] = filters.paginationData.limit;
            } else {
                pagination['first'] = filters.paginationData.limit;
            }
        } else {
            pagination = {
                first: filters.paginationData.limit
            }
        }

        //COLUMNS
        let columns        = ['dateRange'];
        if (filters.columns && filters.columns.length > 0) {
            let realColumns = filters.columns.filter(column => column.virtualField !== true);
            realColumns = realColumns.filter(column => column.visible === true);
            realColumns = realColumns.map(value => value.field);

            realColumns.forEach( value => {
                switch(value) {
                    case 'quarter': 
                    case 'week':
                    case 'month':
                    columns.push(value + '{start end}');
                        break; 

                    default:
                        columns.push(value);
                        break;
                }
            });            
        }

        //SORTING
        let sorting = [];
        let filterSorting = [];
        if (lodash.isObject(filters.sorting)) {
            filterSorting = [filters.sorting];
        } else {
            filterSorting = filters.sorting;
        }

        filterSorting.forEach((value) => {
            if (value.sortColumn && value.sortDirection) {
                sorting.push({ [value.sortColumn]: value.sortDirection });
            }
        });

        function excludeEmptyFieldsFromSorting(sorting)
        {
            return sorting.filter(function(sort) { 
                let count = 0;
                for( let field in sort ) {
                    if( columns.indexOf(field) !== -1 ) {
                        count++;
                    }
                }
                return count > 0;
            });
        }

        sorting = excludeEmptyFieldsFromSorting(sorting);
        if( sorting.length == 0 ) {//DEFAULT SORT
            sorting = [ 
                { 'hour' : 'ASC'},
                { 'date' : 'ASC'},
                { 'boughtClicks' : 'DESC'}, 
                { 'soldClicks' : 'DESC'}, 
            ];
            sorting = excludeEmptyFieldsFromSorting(sorting);
        }            
        

        //REPORT FILTERS
        let usedFilters = getFilters(filters);

        //DO QUERY
        let args = Object.assign(
            { filters: usedFilters, orderBy: sorting },
            pagination
        );

        let qb = new QueryBuilder();
        let reportQuery = qb.connection('scrubRateHourlyReport')
            .fields(columns.join(','))
            .args(args) 
            .edgesQuery();

        return GQ.query({report: reportQuery})
            .catch(function (errorResponse) {
                let message = [];
                errorResponse.errors.forEach(function (error) {
                    message.push(error.message);
                })

                Vue.prototype.$notification.showWarning({
                    title: 'API Error',
                    message: message.join("<br/>"),
                    timeout: 10000
                });
            })
    },


    getSummaryDataFromAPI({ dispatch, state }, filters) {

        if (!filters.isBaseDataLoaded) {
            return new Promise(function (resolve, reject) {
                return resolve({});
            });
        }

        //REPORT FILTERS
        let usedFilters = getFilters(filters);

        //COLUMNS
        let columns = '';
        let groupColumns = [];
        if (filters.columns && filters.columns.length > 0) {
            let realColumns = filters.columns.filter(column => column.virtualField !== true);
            realColumns = realColumns.filter(column => column.visible === true);
            realColumns = realColumns.map(value => value.field);
            
            let selectedColumns = [];
            realColumns.forEach( value => {
                switch(value) {
                    case 'date':
                    case 'year': 
                    case 'hour':                     
                        groupColumns.push(value);
                        break;   

                    case 'boughtClicks':
                    case 'totalInboundClicks': 
                    case 'nonBotInboundClicks':
                    case 'totalInboundScrubRate': 
                    case 'nonBotInboundScrubRate':
                    case 'soldClicks':
                    case 'totalOutboundClicks': 
                    case 'nonBotOutboundClicks':
                    case 'totalOutboundScrubRate': 
                    case 'nonBotOutboundScrubRate':
                    case 'opCtr':
                    case 'nonBotOpCtr':
                    case 'totalOpCtr': 
                        selectedColumns.push(value);
                        break;

                    case 'quarter': 
                    case 'week':
                    case 'month':
                        groupColumns.push(value + 'Index');
                        break; 

                    case 'partnersNames': 
                        // do nothing
                        break;
                                        
                    default:
                        groupColumns.push(value);
                }
            });
            
            columns = `
                dateRange
                total {
                    ` + selectedColumns.join(' ') + `
                }
                average {
                    ` + selectedColumns.join(' ') + `
                }
            `;                       
        }        

        //DO QUERY
        let qb    = new QueryBuilder();
        let query = qb.connection('scrubRateHourlySummaryReport')
            .fields(columns)
            .args({ filters: usedFilters, groupBy: groupColumns }) 
            .query();
            
        return doQuery(query)
            .then(function(result){
                return { summary: { edges : result } };
            })
            .catch(function (errorResponse) {
                let message = [];
                errorResponse.errors.forEach(function (error) {
                    message.push(error.message);
                })

                Vue.prototype.$notification.showWarning({
                    title: 'API Error',
                    message: message.join("<br/>"),
                    timeout: 10000
                });
            })
    },

    setSiteGroupsList({dispatch, state, commit}, value) {
        commit('setSiteGroupsList', value);
    },

    setFilteredSiteGroupsList({dispatch, state, commit}, value) {
        commit('setFilteredSiteGroupsList', value);
        dispatch('setFilteredSiteGroupsObjectList', value);
    },

    setFilteredSiteGroupsObjectList({dispatch, state, rootGetters, commit}, value) {

        if (typeof value  === 'undefined') {
            commit('setFilteredSiteGroupsObjectList', []);
            return;
        }

        let that = this;

        let objects = value.map(function(item){
            let object = that.getters[namespace + '/getSiteGroupsList'].filter(siteGroup => siteGroup.id == item);
            return object[0];
        });

        commit('setFilteredSiteGroupsObjectList', objects);
    },

    setNetworkList({dispatch, state, commit}, value) {
        commit('setNetworkList', value);
    },

    setFilteredNetworkList({dispatch, state, commit}, value) {
        commit('setFilteredNetworkList', value);
        dispatch('setFilteredNetworkObjectList', value);
    },

    setFilteredNetworkObjectList({dispatch, state, rootGetters, commit}, value) {

        if (typeof value  === 'undefined') {
            commit('setFilteredNetworkObjectList', []);
            return;
        }

        let that = this;

        let objects = value.map(function(item){
            let object = that.getters[namespace + '/getNetworkList'].filter(network => network.id == item);

            return object[0];
        });

        commit('setFilteredNetworkObjectList', objects);
    },
};

var queryResults = {};
function doQuery(query)
{
    let _hash = hash(query);

    if( queryResults.hasOwnProperty(_hash) ) {
        return new Promise(function (resolve, reject) {
            return resolve(queryResults[_hash]);
        });
    }

    return GQ.query({query: query})
        .then(function(result){
            queryResults[_hash] = result.query;
            return result.query;
        });
}

function getFilters(filters)
{
    let reportFilters = filters.reportFilters.filters;

    let usedFilters = {};
    if( reportFilters.dateRange.endDate &&  reportFilters.dateRange.startDate ) {
        usedFilters.dateRangesIn = [ [reportFilters.dateRange.startDate.format("YYYY-MM-DD"), reportFilters.dateRange.endDate.format("YYYY-MM-DD")]];
    }

    if( reportFilters.dateRange.endDateB &&  reportFilters.dateRange.startDateB ) {
        usedFilters.dateRangesIn.push([reportFilters.dateRange.startDateB.format("YYYY-MM-DD"), reportFilters.dateRange.endDateB.format("YYYY-MM-DD")]);
    }

    if( reportFilters.dateRange.excludedDates.length > 0 ) {
        usedFilters.dateNotIn = reportFilters.dateRange.excludedDates.map(item => item.format("YYYY-MM-DD"));
    }

    let map = {
        deviceId: 'deviceIdIn',
        siteId : 'siteIdIn',
        marketId : 'marketIdIn',
        partnerId : 'partnerIdIn',
        sourceId : 'sourceIdIn',
        networkId: 'networkIdIn',
        siteGroupId: 'siteGroupIdIn'
    }

    for( let key in map ) {
        if( ! map.hasOwnProperty(key) ) continue;
        let field = map[key];

        if( ! reportFilters.hasOwnProperty(key) || reportFilters[key].length == 0 ) continue;
        usedFilters[field] = reportFilters[key];
    }

    let advancedFilters = filters.reportFilters.advancedFilters;
    
    if( advancedFilters.campaignName && advancedFilters.campaignName.toString().length > 0 ) {
        usedFilters['campaignName'] = advancedFilters.campaignName;
    }

    if( advancedFilters.adGroupName && advancedFilters.adGroupName.toString().length > 0 ) {
        usedFilters['adGroupName'] = advancedFilters.adGroupName;
    }

    if( advancedFilters.hours ) {
        let min = parseInt(advancedFilters.hours.min);
        if(isNaN(min)) min = 0; if( min < 0 ) min = 0; if( min > 23 ) min = 23;
        
        let max = parseInt(advancedFilters.hours.max);
        if(isNaN(max)) max = 24; if( max < 1 ) max = 1; if( max > 24 ) max = 24;
        
        if( min != 0 || max != 24 ) {        
            usedFilters['hourInRange'] = [min, max - 1];
        }
    }

    return usedFilters;
}


// WEBPACK FOOTER //
// ./src/store/modules/scrubRateHourlyReport/actions.js